import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Moment } from 'moment'
import {
  getCompletedQuarter,
  getPeriodFromQuarterAndYear,
  getQuarterAndYear
} from '../../../utils/moment'
import useRouter from '../../../hooks/useRouter'
import { routes } from '../../../router'
import { FINANCIAL_TABS } from '../constants'
import { getPaymentStatusData } from '../CognusAR/api'
import { getCognusARTableData } from '../CognusAR/CognusARTable/api'
import { getReportsData } from '../utils'
import { INITIAL_DATA_REPORTS_LEGEND_WITH_PARTIAL } from '../constants'
import { setLoading } from '../../../redux/store/common/slice'
import { DEFAULT_PAGE } from '../../../components/Table/constants'
import {
  CognusARData,
  CognusARRequestData,
  CognusARTableParams
} from '../CognusAR/CognusARTable/types'
import { PaymentStatus } from '../types'
import { TPageInfo } from '../../../components/Table/types'
import { getUser } from '../../../redux/store/user/getters'
import { useFinancialsAbility } from './FinancialsAbilityProvider'
import { ACTIONS, SUBJECTS, contractSubject } from '../Abilities'

type ContextProps = {
  state: {
    totalNumber: number
    totalAmount: number
    firstLoad: boolean
    pageInfo: TPageInfo
    period: Moment
    tableData: CognusARData[]
    reports: PaymentStatus[]
    showHSName: boolean
    canSeeInvoicesDetailsOnMarketplaceAR: boolean
    canInitiateDispute: boolean
  }
  actions: {
    setPeriod: Dispatch<SetStateAction<Moment>>
    setPageInfo: Dispatch<SetStateAction<TPageInfo>>
    getTableData: (args: CognusARRequestData) => Promise<void>
    getChartsData: () => Promise<void>
    updateCognusARData: () => Promise<void>
  }
}

const FinancialsCognusARContext = createContext<ContextProps>({
  state: {} as ContextProps['state'],
  actions: {} as ContextProps['actions']
})

const FinancialsCognusARProvider: FC<PropsWithChildren> = ({ children }) => {
  const { query, pathname } = useRouter()
  const [totalAmount, setTotalAmount] = useState(0)
  const [totalNumber, setTotalNumber] = useState(0)
  const [firstLoad, setFirstLoad] = useState(true)
  const [period, setPeriod] = useState<Moment>(() => {
    const periodFromQuery = getPeriodFromQuarterAndYear(
      query.year,
      query.quarter
    )
    const isMarketplaceAR = pathname.includes(
      `${routes.financials}/${FINANCIAL_TABS[2].key}`
    )

    return isMarketplaceAR && periodFromQuery
      ? periodFromQuery
      : getCompletedQuarter()
  })
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const [tableData, setTableData] = useState<CognusARData[]>([])
  const [reports, setReports] = useState<PaymentStatus[]>([])
  const dispatch = useDispatch()
  const user = useSelector(getUser)
  const ability = useFinancialsAbility()

  const showHSName = useMemo(
    () =>
      ability.can(
        ACTIONS.VIEW,
        contractSubject(SUBJECTS.COGNUS_AR_HS_NAME, user)
      ),
    [ability, user]
  )

  const canSeeInvoicesDetailsOnMarketplaceAR = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.COGNUS_AR_INVOICES_DETAILS),
    [ability]
  )

  const canInitiateDispute = useMemo(
    () =>
      ability.can(
        ACTIONS.INITIATE,
        SUBJECTS.COGNUS_AR_INVOICES_INITIATE_DISPUTE
      ),
    [ability]
  )

  const getTableData = useCallback(
    async ({ page }: CognusARRequestData) => {
      try {
        dispatch(setLoading(true))
        const params: CognusARTableParams = {
          ...getQuarterAndYear(period)
        }
        const dataPage = page ? page : DEFAULT_PAGE
        params.limit = dataPage.pageSize
        params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
        const { data } = await getCognusARTableData({ params })
        setTableData(
          data.results.map((row) => ({
            ...row,
            key: row.uuid
          }))
        )
        setTotalNumber(data.count)
        if (firstLoad) {
          setFirstLoad(false)
        }
      } finally {
        dispatch(setLoading(false))
      }
    },
    [dispatch, firstLoad, period]
  )

  const getChartsData = useCallback(async () => {
    try {
      dispatch(setLoading(true))
      const { data } = await getPaymentStatusData({
        params: getQuarterAndYear(period)
      })
      const results = getReportsData(
        data.results,
        INITIAL_DATA_REPORTS_LEGEND_WITH_PARTIAL
      )
      setReports(results)
      setTotalAmount(results.reduce((prev, cur) => prev + cur.amount, 0))
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch, period])

  const updateCognusARData = useCallback(async () => {
    await getTableData({ page: pageInfo })
    await getChartsData()
  }, [getChartsData, getTableData, pageInfo])

  const context = useMemo(
    () => ({
      state: {
        totalNumber,
        tableData,
        firstLoad,
        totalAmount,
        reports,
        period,
        pageInfo,
        showHSName,
        canSeeInvoicesDetailsOnMarketplaceAR,
        canInitiateDispute
      },
      actions: {
        setPeriod,
        setPageInfo,
        getTableData,
        getChartsData,
        updateCognusARData
      }
    }),
    [
      totalNumber,
      tableData,
      firstLoad,
      totalAmount,
      reports,
      period,
      pageInfo,
      showHSName,
      getTableData,
      getChartsData,
      updateCognusARData,
      canSeeInvoicesDetailsOnMarketplaceAR,
      canInitiateDispute
    ]
  )

  return (
    <FinancialsCognusARContext.Provider value={context}>
      {children}
    </FinancialsCognusARContext.Provider>
  )
}

export const useFinancialsCognusARContext = () =>
  useContext(FinancialsCognusARContext)

export default FinancialsCognusARProvider
